import { useState } from 'react';
import { motion } from 'framer-motion';
import { UserCheck, Calendar, Loader2, AlertCircle, Users } from 'lucide-react';
import { Header, VesselCategories, CrewCard, CrewDetailModal } from '../components';
import { useAvailableCrew, useSummary } from '../hooks/useSearch';
import type { CrewMember } from '../types';

export default function AvailablePage() {
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedCrew, setSelectedCrew] = useState<CrewMember | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data: availableCrew, isLoading, error } = useAvailableCrew();
  const { data: summary } = useSummary();

  const handleCategoryClick = (category: string) => {
    setSelectedCategory(category === selectedCategory ? '' : category);
  };

  const handleViewDetails = (crew: CrewMember) => {
    setSelectedCrew(crew);
    setIsModalOpen(true);
  };

  // Calculate availability stats based on due dates
  const now = new Date();
  const in7Days = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  const in14Days = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);
  const in30Days = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

  const availableIn7Days = availableCrew?.filter(c => {
    if (!c.due_date) return true;
    return new Date(c.due_date) <= in7Days;
  }).length || 0;

  const availableIn14Days = availableCrew?.filter(c => {
    if (!c.due_date) return true;
    return new Date(c.due_date) <= in14Days;
  }).length || 0;

  const availableIn30Days = availableCrew?.filter(c => {
    if (!c.due_date) return true;
    return new Date(c.due_date) <= in30Days;
  }).length || 0;

  // Filter crew by category if selected
  const filteredCrew = selectedCategory
    ? availableCrew?.filter(c => 
        c.crew_type?.toLowerCase().includes(selectedCategory.toLowerCase()) ||
        c.vessel_type?.toLowerCase().includes(selectedCategory.toLowerCase())
      )
    : availableCrew;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-11 h-11 rounded-xl bg-green-100 flex items-center justify-center">
              <UserCheck className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Available Crew</h1>
              <p className="text-gray-500">
                {summary?.total_available || availableCrew?.length || 0} crew members ready for assignment
              </p>
            </div>
          </div>
        </motion.div>

        {/* Available Crew by Category */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card mb-8"
        >
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Search by Vessel Category</h2>
          <VesselCategories 
            onCategoryClick={handleCategoryClick}
            selectedCategory={selectedCategory}
          />
        </motion.div>

        {/* Upcoming Availability */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card mb-8"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-amber-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Upcoming Availability</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 rounded-lg bg-green-50 border border-green-100">
              <h3 className="text-sm font-medium text-gray-700 mb-1">Next 7 Days</h3>
              <p className="text-2xl font-bold text-green-600">{availableIn7Days}</p>
              <p className="text-sm text-gray-500">crew members</p>
            </div>
            <div className="p-4 rounded-lg bg-amber-50 border border-amber-100">
              <h3 className="text-sm font-medium text-gray-700 mb-1">Next 14 Days</h3>
              <p className="text-2xl font-bold text-amber-600">{availableIn14Days}</p>
              <p className="text-sm text-gray-500">crew members</p>
            </div>
            <div className="p-4 rounded-lg bg-blue-50 border border-blue-100">
              <h3 className="text-sm font-medium text-gray-700 mb-1">Next 30 Days</h3>
              <p className="text-2xl font-bold text-blue-600">{availableIn30Days}</p>
              <p className="text-sm text-gray-500">crew members</p>
            </div>
          </div>
        </motion.div>

        {/* Loading State */}
        {isLoading && (
          <div className="text-center py-12">
            <Loader2 className="w-10 h-10 text-primary-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-500">Loading available crew from database...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="card text-center py-12 border-red-200">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to Load Available Crew</h3>
            <p className="text-gray-500">{error.message}</p>
          </div>
        )}

        {/* Crew List */}
        {filteredCrew && filteredCrew.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <Users className="w-5 h-5 text-gray-500" />
              <h2 className="text-lg font-semibold text-gray-900">
                {selectedCategory ? `${selectedCategory} Vessel Crew` : 'All Available Crew'}
              </h2>
              <span className="text-sm text-gray-500">({filteredCrew.length} members)</span>
            </div>
            <div className="space-y-3">
              {filteredCrew.slice(0, 20).map((crew, index) => (
                <CrewCard
                  key={crew.cid}
                  crew={crew}
                  index={index}
                  onViewDetails={handleViewDetails}
                />
              ))}
            </div>
            {filteredCrew.length > 20 && (
              <p className="text-center text-gray-500 mt-4">
                Showing 20 of {filteredCrew.length} available crew members
              </p>
            )}
          </motion.div>
        )}

        {/* Empty State */}
        {filteredCrew && filteredCrew.length === 0 && !isLoading && (
          <div className="card text-center py-12">
            <UserCheck className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Available Crew Found</h3>
            <p className="text-gray-500">
              {selectedCategory 
                ? `No crew members available for ${selectedCategory} vessels.`
                : 'There are no available crew members in the database.'}
            </p>
          </div>
        )}
      </main>

      {/* Crew Detail Modal */}
      <CrewDetailModal
        crew={selectedCrew}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
}
